import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth, isAdmin } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatDateTime, plural } from "@/lib/utils";
import { ChevronLeft, ChevronRight, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const PAGE = 50;
const ALL = "all";

// The table is append-only and trigger-protected, so this screen is the whole of what a
// person can do with it: read, narrow, and page. There is deliberately no export — the
// register exports are the documents that leave the building.
export default function AuditLog() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [action, setAction] = useState(ALL);
  const [entity, setEntity] = useState(ALL);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE, offset };
      if (action !== ALL) params.action = action;
      if (entity !== ALL) params.entity_type = entity;
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      const r = await api.get("/audit-log", { params });
      setRows(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Could not load the audit log"));
    }
    setLoading(false);
  }, [offset, action, entity, from, to]);

  useEffect(() => { load(); }, [load]);
  // Any change to what is being asked for starts again at the first page, or page 4 of
  // the old query silently becomes page 4 of the new one.
  const narrow = (set) => (v) => { setOffset(0); set(v); };

  if (!isAdmin(user)) {
    return (
      <Card className="p-8 border border-border text-center" data-testid="audit-denied">
        <ShieldAlert className="w-6 h-6 mx-auto text-muted-foreground" />
        <div className="font-heading font-semibold mt-3">Administrators only</div>
        <p className="text-sm text-muted-foreground mt-1">
          The audit log records who changed what. Only an Admin can read it.
        </p>
      </Card>
    );
  }

  // Drawn from what is on the page: the vocabulary grows as routes are added, and a
  // hardcoded list would quietly stop offering the newest actions.
  const seen = (key) => [...new Set(rows.map((r) => r[key]).filter(Boolean))].sort();
  const page = Math.floor(offset / PAGE) + 1;
  const pages = Math.max(1, Math.ceil(total / PAGE));

  return (
    <div className="space-y-5" data-testid="audit-page">
      <div>
        <div className="overline text-primary">Accountability</div>
        <h2 className="font-heading font-bold text-3xl mt-1">Audit Log</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Every write, in order, with who made it. The table is append-only — entries cannot be
          edited or removed, including from here.
        </p>
      </div>

      <Card className="p-4 border border-border">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="audit-action">Action</Label>
            <Select value={action} onValueChange={narrow(setAction)}>
              <SelectTrigger id="audit-action" data-testid="audit-filter-action"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All actions</SelectItem>
                {seen("action").map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="audit-entity">Record type</Label>
            <Select value={entity} onValueChange={narrow(setEntity)}>
              <SelectTrigger id="audit-entity" data-testid="audit-filter-entity"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All records</SelectItem>
                {seen("entity_type").map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="audit-from">From</Label>
            <Input id="audit-from" type="date" value={from} onChange={(e) => narrow(setFrom)(e.target.value)} data-testid="audit-from" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="audit-to">To</Label>
            <Input id="audit-to" type="date" value={to} onChange={(e) => narrow(setTo)(e.target.value)} data-testid="audit-to" />
          </div>
        </div>
      </Card>

      <Card className="border border-border overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="text-sm text-muted-foreground" data-testid="audit-count">
            {loading ? "Loading..." : plural(total, "entry", "entries")}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" aria-label="Previous page" data-testid="audit-prev"
                    disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="text-sm w-28 text-center">Page {page} of {pages}</div>
            <Button variant="outline" size="icon" aria-label="Next page" data-testid="audit-next"
                    disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/60 text-left">
              <tr>
                <th className="px-4 py-2 font-medium">When</th>
                <th className="px-4 py-2 font-medium">Who</th>
                <th className="px-4 py-2 font-medium">Action</th>
                <th className="px-4 py-2 font-medium">Record</th>
                <th className="px-4 py-2 font-medium">Detail</th>
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">
                  Nothing recorded for this filter.
                </td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-border last:border-0 align-top" data-testid={`audit-row-${r.id}`}>
                  <td className="px-4 py-2 whitespace-nowrap text-muted-foreground">{formatDateTime(r.created_at)}</td>
                  <td className="px-4 py-2 whitespace-nowrap font-medium">{r.user_name || "—"}</td>
                  <td className="px-4 py-2 whitespace-nowrap font-mono text-xs">{r.action}</td>
                  <td className="px-4 py-2 whitespace-nowrap text-muted-foreground">{r.entity_type}</td>
                  <td className="px-4 py-2 text-xs text-muted-foreground max-w-md">
                    <Detail value={r.details} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// Details are free-shaped per route. Rendering the raw object gave a wall of JSON, so the
// short values are read out and anything long stays behind a disclosure.
function Detail({ value }) {
  const [open, setOpen] = useState(false);
  if (value == null || value === "") return <span>—</span>;
  const obj = typeof value === "string" ? safeParse(value) : value;
  if (obj == null || typeof obj !== "object") return <span className="break-words">{String(value)}</span>;

  const entries = Object.entries(obj);
  const short = entries.filter(([, v]) => v == null || typeof v !== "object");
  const long = entries.filter(([, v]) => v != null && typeof v === "object");

  return (
    <div className="space-y-1">
      {short.map(([k, v]) => (
        <div key={k}>
          <span className="text-muted-foreground/70">{k}:</span>{" "}
          {/* An em dash, the way the empty case above is already drawn. String(v) put the
              word "null" in front of an auditor for every column that was not filled in,
              and rendered an empty string as an invisible gap after the colon. `false`
              stays as it is — for is_deleted that is the answer, not the absence of one. */}
          {v === null || v === undefined || v === ""
            ? <span className="text-muted-foreground">—</span>
            : <span className="text-foreground">{String(v)}</span>}
        </div>
      ))}
      {long.length > 0 && (
        <>
          <button onClick={() => setOpen(!open)} className="text-primary hover:underline underline-offset-2">
            {open ? "Hide" : `Show ${plural(long.length, "more field")}`}
          </button>
          {open && (
            <pre className="mt-1 p-2 rounded bg-muted/50 overflow-x-auto whitespace-pre-wrap break-words">
              {JSON.stringify(Object.fromEntries(long), null, 1)}
            </pre>
          )}
        </>
      )}
    </div>
  );
}

function safeParse(s) {
  try { return JSON.parse(s); } catch { return s; }
}
